import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, useUnit, api } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { canCreateTickets } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { 
  TicketIcon, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Plus,
  ArrowRight,
  TrendingUp,
  Store
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const STATUS_COLORS = {
  "Troubleshooting in Progress": { bg: "#DBEAFE", text: "#1E40AF" },
  "Escalated to Help Desk": { bg: "#DBEAFE", text: "#1E40AF" },
  "Assigned to Technician": { bg: "#FEF9C3", text: "#854D0E" },
  "Vendor Assigned": { bg: "#FEF9C3", text: "#854D0E" },
  "Pending Cost Approval": { bg: "#FFEDD5", text: "#9A3412" },
  "Repair Authorized": { bg: "#EEF2FF", text: "#4338CA" },
  "Resolved": { bg: "#DCFCE7", text: "#166534" },
  "Resolved by Manager": { bg: "#DCFCE7", text: "#166534" },
  "Rejected - Needs Review": { bg: "#FEE2E2", text: "#991B1B" },
  "Cancelled": { bg: "#F3F4F6", text: "#6B7280" },
};

const PIE_COLORS = ["#4F46E5", "#EAB308", "#EA580C", "#16A34A", "#DC2626"];

export default function Dashboard() {
  const { unitId } = useParams();
  const { user } = useAuth();
  const { setCurrentUnit } = useUnit();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Paid-date filter (default: last 30 days)
  const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const daysAgoStr = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const [paidFrom, setPaidFrom] = useState(daysAgoStr(30));
  const [paidTo, setPaidTo] = useState(todayStr());

  useEffect(() => {
    // Set current unit context
    const units = [
      { id: "qnc", name: "QNC", full_name: "Quick N Clean Operations" },
      { id: "diner_dfw", name: "DINER DFW", full_name: "Dallas-Fort Worth Region" },
      { id: "diner_fl", name: "DINER FL", full_name: "Florida Region" },
      { id: "textaco", name: "Textaco", full_name: "Textaco Locations" },
    ];
    const unit = units.find(u => u.id === unitId);
    if (unit) setCurrentUnit(unit);
    
    fetchDashboardData();
  }, [unitId, paidFrom, paidTo]);

  const fetchDashboardData = async () => {
    try {
      const params = new URLSearchParams({ unit_id: unitId, paid_from: paidFrom, paid_to: paidTo });
      const [statsRes, recentRes] = await Promise.all([
        api.get(`/dashboard/stats?${params.toString()}`),
        api.get(`/dashboard/recent?unit_id=${unitId}&limit=5`)
      ]);
      setStats(statsRes.data);
      setRecentTickets(recentRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyle = (status) => {
    const style = STATUS_COLORS[status] || { bg: "#F3F4F6", text: "#6B7280" };
    return { backgroundColor: style.bg, color: style.text };
  };

  const getPriorityStyle = (priority) => {
    const styles = {
      P1: "priority-p1",
      P2: "priority-p2",
      P3: "priority-p3",
      P4: "priority-p4",
    };
    return styles[priority] || "priority-p4";
  };

  // Prepare chart data
  const statusChartData = stats?.by_status 
    ? Object.entries(stats.by_status).map(([name, value]) => ({ name: name.split(' ')[0], value }))
    : [];

  const categoryChartData = stats?.by_category || [];
  const monthlyChartData = stats?.by_month || [];

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar unitId={unitId} />
        <main className="flex-1 p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar unitId={unitId} />
      
      <main className="flex-1 p-4 md:p-8 overflow-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 animate-fade-in">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Dashboard</h1>
            <p className="text-gray-600 mt-1">Overview of maintenance operations</p>
          </div>
          {canCreateTickets(user?.role) && (
            <Link to={`/new-ticket/${unitId}`}>
              <Button className="mt-4 sm:mt-0 bg-indigo-600 hover:bg-indigo-700" data-testid="new-ticket-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Ticket
              </Button>
            </Link>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <Card className="animate-slide-up stagger-1 bg-white border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Open Tickets</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1 tabular-nums">{stats?.open || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TicketIcon className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="animate-slide-up stagger-2 bg-white border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">In Progress</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1 tabular-nums">{stats?.in_progress || 0}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="animate-slide-up stagger-3 bg-white border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Pending Approval</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1 tabular-nums">{stats?.pending_approval || 0}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="animate-slide-up stagger-4 bg-white border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Resolved</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1 tabular-nums">{stats?.resolved || 0}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Status Distribution */}
          <Card className="animate-slide-up bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-900">By Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${value}`}
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Trend */}
          <Card className="lg:col-span-2 animate-slide-up bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
                Monthly Tickets
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#6B7280" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#6B7280" />
                    <Tooltip />
                    <Bar dataKey="count" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Store Summary */}
        {stats?.by_store && stats.by_store.length > 0 && (
          <Card className="animate-slide-up mb-8 bg-white border-gray-200">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Store className="w-5 h-5 text-indigo-600" />
                  By Store
                </CardTitle>
                <div className="flex flex-wrap items-end gap-2 text-sm">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Paid from</label>
                    <input
                      type="date"
                      value={paidFrom}
                      max={paidTo || todayStr()}
                      onChange={(e) => setPaidFrom(e.target.value)}
                      className="border border-gray-200 rounded-md px-2 py-1 text-sm"
                      data-testid="paid-from-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">to</label>
                    <input
                      type="date"
                      value={paidTo}
                      max={todayStr()}
                      min={paidFrom}
                      onChange={(e) => setPaidTo(e.target.value)}
                      className="border border-gray-200 rounded-md px-2 py-1 text-sm"
                      data-testid="paid-to-input"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => { setPaidFrom(daysAgoStr(30)); setPaidTo(todayStr()); }}
                    className="text-xs text-indigo-600 hover:text-indigo-700 px-2 py-1.5"
                    data-testid="paid-reset-30"
                  >
                    Last 30d
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPaidFrom(daysAgoStr(90)); setPaidTo(todayStr()); }}
                    className="text-xs text-indigo-600 hover:text-indigo-700 px-2 py-1.5"
                  >
                    Last 90d
                  </button>
                </div>
              </div>
              {stats?.paid_filter && (
                <p className="text-sm text-gray-600 mt-2" data-testid="paid-filter-summary">
                  <span className="font-medium text-gray-900">{stats.paid_filter.count}</span> ticket(s) paid between{" "}
                  <span className="font-mono">{stats.paid_filter.from}</span> and{" "}
                  <span className="font-mono">{stats.paid_filter.to}</span> ·
                  total <span className="font-medium text-emerald-700">${Number(stats.paid_filter.total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </p>
              )}
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="store-summary-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2.5 px-4 text-sm font-medium text-gray-500">Store</th>
                      <th className="text-center py-2.5 px-4 text-sm font-medium text-gray-500">Total</th>
                      <th className="text-center py-2.5 px-4 text-sm font-medium text-gray-500">Open</th>
                      <th className="text-center py-2.5 px-4 text-sm font-medium text-gray-500">Resolved</th>
                      <th className="text-center py-2.5 px-4 text-sm font-medium text-gray-500">Paid (in range)</th>
                      <th className="text-right py-2.5 px-4 text-sm font-medium text-gray-500">Paid Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.by_store.map((s) => (
                      <tr key={s.store} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-2.5 px-4 font-medium text-gray-900 text-sm">{s.store}</td>
                        <td className="py-2.5 px-4 text-center">
                          <Badge variant="outline" className="text-xs">{s.total}</Badge>
                        </td>
                        <td className="py-2.5 px-4 text-center">
                          {s.open > 0 ? (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">{s.open}</Badge>
                          ) : (
                            <span className="text-xs text-gray-400">0</span>
                          )}
                        </td>
                        <td className="py-2.5 px-4 text-center">
                          {s.resolved > 0 ? (
                            <Badge className="bg-green-100 text-green-700 text-xs">{s.resolved}</Badge>
                          ) : (
                            <span className="text-xs text-gray-400">0</span>
                          )}
                        </td>
                        <td className="py-2.5 px-4 text-center">
                          {s.paid_in_range_count > 0 ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">{s.paid_in_range_count}</Badge>
                          ) : (
                            <span className="text-xs text-gray-400">0</span>
                          )}
                        </td>
                        <td className="py-2.5 px-4 text-right text-sm font-medium text-gray-700">
                          ${Number(s.paid_in_range_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Tickets */}
        <Card className="animate-slide-up bg-white border-gray-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-gray-900">Recent Tickets</CardTitle>
            <Link 
              to={`/tickets/${unitId}`}
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
              data-testid="view-all-tickets-link"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {recentTickets.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4">Job Number</th>
                      <th className="text-left py-3 px-4">Restaurant</th>
                      <th className="text-left py-3 px-4">Equipment</th>
                      <th className="text-left py-3 px-4">Priority</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTickets.map((ticket) => (
                      <tr 
                        key={ticket.id} 
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/ticket/${ticket.id}`)}
                        data-testid={`ticket-row-${ticket.id}`}
                      >
                        <td className="py-3 px-4 font-medium text-indigo-600">{ticket.job_number}</td>
                        <td className="py-3 px-4 text-gray-900">{ticket.restaurant_name}</td>
                        <td className="py-3 px-4 text-gray-600">{ticket.equipment_category}</td>
                        <td className="py-3 px-4">
                          {ticket.priority && (
                            <Badge className={`${getPriorityStyle(ticket.priority)} text-xs`}>
                              {ticket.priority}
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge 
                            className="text-xs whitespace-nowrap"
                            style={getStatusStyle(ticket.status)}
                          >
                            {ticket.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-sm">
                          {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <TicketIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No tickets yet</p>
                {canCreateTickets(user?.role) && (
                  <Link to={`/new-ticket/${unitId}`}>
                    <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700">
                      Create First Ticket
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
